import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import logoIcon from "@/assets/logo-warmzapp.webp";
import {
  Rocket, QrCode, Globe, Smartphone, ChevronRight, ChevronLeft,
  SkipForward, Sparkles, Shield, Zap, Check, Plus,
} from "lucide-react";

const presets = [
  { id: "cold", emoji: "❄️", name: "Frio", desc: "0–7 dias · Segurança máxima", color: "border-blue-500/30 bg-blue-500/10 text-blue-400" },
  { id: "warm", emoji: "🟡", name: "Morno", desc: "8–15 dias · Uso ativo", color: "border-yellow-500/30 bg-yellow-500/10 text-yellow-400" },
  { id: "hot", emoji: "🔥", name: "Quente", desc: "15+ dias · Tráfego pesado", color: "border-red-500/30 bg-red-500/10 text-red-400" },
  { id: "maintenance", emoji: "🛡️", name: "Manutenção", desc: "Em operação · Reequilibrar", color: "border-accent/30 bg-accent/10 text-accent" },
];

// ── Step indicator ──
function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-1.5 rounded-full transition-all duration-500 ${
            i === current ? "w-8 bg-primary" : i < current ? "w-4 bg-primary/40" : "w-4 bg-muted"
          }`}
        />
      ))}
    </div>
  );
}

// ── Slide wrapper ──
function Slide({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -40 }}
      transition={{ duration: 0.35, ease: "easeInOut" }}
      className="w-full"
    >
      {children}
    </motion.div>
  );
}

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [preset1, setPreset1] = useState("cold");
  const [globalNetwork, setGlobalNetwork] = useState(false);
  const [addSecond, setAddSecond] = useState<null | boolean>(null);
  const [preset2, setPreset2] = useState("cold");
  const totalSteps = 4;

  const goToDashboard = () => navigate("/dashboard");
  const next = () => setStep((s) => Math.min(s + 1, totalSteps - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="w-full max-w-2xl space-y-8 relative z-10">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-center gap-2"
        >
          <img src={logoIcon} alt="WarmZapp" className="h-9 w-auto object-contain" />
          <span className="font-display text-xl font-bold text-foreground">
            Warm<span className="text-primary">Zapp</span>
          </span>
        </motion.div>

        {/* Step indicator */}
        <div className="flex justify-center">
          <StepIndicator current={step} total={totalSteps} />
        </div>

        {/* Steps */}
        <AnimatePresence mode="wait">
          {/* ── Step 0: Welcome ── */}
          {step === 0 && (
            <Slide key="welcome">
              <Card className="glass-card border-warmz-glass-border/50 overflow-hidden">
                <CardContent className="p-8 md:p-12 text-center space-y-6">
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.15 }}
                    className="w-20 h-20 mx-auto rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center"
                  >
                    <Rocket className="h-10 w-10 text-primary" />
                  </motion.div>

                  <div className="space-y-3">
                    <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground">
                      Bem-vindo ao WarmZapp! 🎉
                    </h1>
                    <p className="text-muted-foreground text-base max-w-md mx-auto leading-relaxed">
                      Vamos preparar tudo em poucos passos. Conecte seus chips WhatsApp e comece o aquecimento automático agora mesmo.
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-3 max-w-sm mx-auto pt-2">
                    {[
                      { icon: Zap, label: "Aquecimento IA" },
                      { icon: Shield, label: "Anti-ban" },
                      { icon: Globe, label: "Rede Global" },
                    ].map((f) => (
                      <div key={f.label} className="p-3 rounded-xl bg-secondary/30 border border-warmz-glass-border/30 text-center">
                        <f.icon className="h-5 w-5 text-primary mx-auto mb-1" />
                        <p className="text-[11px] text-muted-foreground">{f.label}</p>
                      </div>
                    ))}
                  </div>

                  <Button size="lg" className="gap-2 glow-orange-sm mt-4" onClick={next}>
                    Começar Configuração
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            </Slide>
          )}

          {/* ── Step 1: Connect 1st number ── */}
          {step === 1 && (
            <Slide key="connect1">
              <Card className="glass-card border-warmz-glass-border/50">
                <CardContent className="p-8 space-y-6">
                  <div className="text-center space-y-2">
                    <Badge variant="outline" className="text-xs border-primary/30 text-primary mb-2">Passo 1 de 3</Badge>
                    <h2 className="font-display text-2xl font-bold text-foreground">Conecte seu primeiro número</h2>
                    <p className="text-muted-foreground text-sm">Escaneie o QR Code com o WhatsApp do chip que deseja aquecer.</p>
                  </div>

                  {/* QR Code */}
                  <div className="flex justify-center">
                    <div className="w-48 h-48 rounded-2xl bg-foreground/95 flex items-center justify-center relative">
                      <QrCode className="h-32 w-32 text-background" />
                      <div className="absolute inset-0 rounded-2xl border-2 border-primary/30 animate-pulse" />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground text-center">WhatsApp → Aparelhos Conectados → Conectar</p>

                  {/* Preset selector */}
                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-foreground">Escolha o modo de aquecimento:</p>
                    <div className="grid grid-cols-2 gap-2">
                      {presets.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setPreset1(p.id)}
                          className={`p-3 rounded-xl border text-left transition-all ${
                            preset1 === p.id
                              ? `${p.color} ring-1 ring-current`
                              : "border-warmz-glass-border/30 bg-secondary/20 hover:bg-secondary/40"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{p.emoji}</span>
                            <div>
                              <p className="text-sm font-medium text-foreground">{p.name}</p>
                              <p className="text-[10px] text-muted-foreground">{p.desc}</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-between pt-2">
                    <Button variant="ghost" onClick={prev} className="gap-1">
                      <ChevronLeft className="h-4 w-4" /> Voltar
                    </Button>
                    <div className="flex gap-2">
                      <Button variant="ghost" onClick={goToDashboard} className="gap-1 text-muted-foreground">
                        <SkipForward className="h-4 w-4" /> Pular tudo
                      </Button>
                      <Button onClick={next} className="gap-1 glow-orange-sm">
                        Continuar <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Slide>
          )}

          {/* ── Step 2: Global Network ── */}
          {step === 2 && (
            <Slide key="global">
              <Card className="glass-card border-warmz-glass-border/50">
                <CardContent className="p-8 space-y-6">
                  <div className="text-center space-y-2">
                    <Badge variant="outline" className="text-xs border-primary/30 text-primary mb-2">Passo 2 de 3</Badge>
                    <h2 className="font-display text-2xl font-bold text-foreground">Rede Global de Aquecimento</h2>
                    <p className="text-muted-foreground text-sm max-w-md mx-auto">
                      A Rede Global conecta seu chip com milhares de outros números reais, gerando conversas naturais e acelerando o aquecimento.
                    </p>
                  </div>

                  <div className="max-w-md mx-auto space-y-4">
                    {/* Benefits */}
                    <div className="grid grid-cols-1 gap-2">
                      {[
                        { icon: Sparkles, text: "Conversas geradas por IA com outros chips reais" },
                        { icon: Shield, text: "Aumenta o Trust Score do WhatsApp rapidamente" },
                        { icon: Globe, text: "Simula interações com números desconhecidos" },
                      ].map((b, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.1 + i * 0.1 }}
                          className="flex items-center gap-3 p-3 rounded-xl bg-secondary/20 border border-warmz-glass-border/30"
                        >
                          <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                            <b.icon className="h-4 w-4 text-accent" />
                          </div>
                          <p className="text-sm text-foreground">{b.text}</p>
                        </motion.div>
                      ))}
                    </div>

                    {/* Toggle */}
                    <div className="flex items-center justify-between p-4 rounded-xl bg-secondary/30 border border-warmz-glass-border/30">
                      <div className="flex items-center gap-3">
                        <Globe className={`h-5 w-5 ${globalNetwork ? "text-accent" : "text-muted-foreground"}`} />
                        <div>
                          <p className="text-sm font-semibold text-foreground">Ativar Rede Global</p>
                          <p className="text-xs text-muted-foreground">+$5/mês por chip</p>
                        </div>
                      </div>
                      <Switch checked={globalNetwork} onCheckedChange={setGlobalNetwork} />
                    </div>

                    {globalNetwork && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="flex items-center gap-2 p-3 rounded-xl bg-accent/10 border border-accent/20"
                      >
                        <Check className="h-4 w-4 text-accent shrink-0" />
                        <p className="text-xs text-accent">Rede Global será ativada no seu primeiro chip!</p>
                      </motion.div>
                    )}
                  </div>

                  <div className="flex justify-between pt-2">
                    <Button variant="ghost" onClick={prev} className="gap-1">
                      <ChevronLeft className="h-4 w-4" /> Voltar
                    </Button>
                    <div className="flex gap-2">
                      <Button variant="ghost" onClick={goToDashboard} className="gap-1 text-muted-foreground">
                        <SkipForward className="h-4 w-4" /> Pular tudo
                      </Button>
                      <Button onClick={next} className="gap-1 glow-orange-sm">
                        Continuar <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Slide>
          )}

          {/* ── Step 3: Add 2nd number ── */}
          {step === 3 && (
            <Slide key="second">
              <Card className="glass-card border-warmz-glass-border/50">
                <CardContent className="p-8 space-y-6">
                  <div className="text-center space-y-2">
                    <Badge variant="outline" className="text-xs border-yellow-500/30 text-yellow-400 mb-2">⚠️ Importante</Badge>
                    <h2 className="font-display text-2xl font-bold text-foreground">Conecte o segundo número</h2>
                    <p className="text-muted-foreground text-sm max-w-md mx-auto">
                      Sem a Rede Global, o aquecimento funciona entre os seus próprios chips. <span className="text-foreground font-medium">Você precisa de pelo menos 2 números conectados</span> para que eles troquem mensagens e o aquecimento comece.
                    </p>
                  </div>

                  <div className="max-w-sm mx-auto space-y-2">
                    <div className="p-3 rounded-xl bg-yellow-500/5 border border-yellow-500/20 flex items-start gap-2.5">
                      <Zap className="h-4 w-4 text-yellow-400 shrink-0 mt-0.5" />
                      <p className="text-xs text-yellow-300/80">Se pular esta etapa, o aquecimento ficará <strong>pausado</strong> até que você conecte o 2º chip na página de Números.</p>
                    </div>
                    <div className="p-3 rounded-xl bg-accent/5 border border-accent/20 flex items-start gap-2.5">
                      <Shield className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                      <p className="text-xs text-accent/80"><strong>Dica:</strong> salve o número de um chip na agenda do outro. Contatos salvos são confiáveis pelo WhatsApp e aceleram o aquecimento.</p>
                    </div>
                  </div>

                  {addSecond === null ? (
                    <div className="flex flex-col items-center gap-3 max-w-sm mx-auto">
                      <Button
                        size="lg"
                        className="w-full gap-2 glow-orange-sm"
                        onClick={() => setAddSecond(true)}
                      >
                        <Plus className="h-4 w-4" />
                        Conectar segundo número agora
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-2 text-muted-foreground"
                        onClick={() => setAddSecond(false)}
                      >
                        <SkipForward className="h-4 w-4" />
                        Pular por enquanto
                      </Button>
                    </div>
                  ) : addSecond ? (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="space-y-5"
                    >
                      {/* QR Code */}
                      <div className="flex justify-center">
                        <div className="w-44 h-44 rounded-2xl bg-foreground/95 flex items-center justify-center relative">
                          <QrCode className="h-28 w-28 text-background" />
                          <div className="absolute inset-0 rounded-2xl border-2 border-primary/30 animate-pulse" />
                        </div>
                      </div>

                      <div className="space-y-3">
                        <p className="text-sm font-semibold text-foreground text-center">Modo de aquecimento do 2º chip:</p>
                        <div className="grid grid-cols-2 gap-2">
                          {presets.map((p) => (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => setPreset2(p.id)}
                              className={`p-3 rounded-xl border text-left transition-all ${
                                preset2 === p.id
                                  ? `${p.color} ring-1 ring-current`
                                  : "border-warmz-glass-border/30 bg-secondary/20 hover:bg-secondary/40"
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-lg">{p.emoji}</span>
                                <div>
                                  <p className="text-sm font-medium text-foreground">{p.name}</p>
                                  <p className="text-[10px] text-muted-foreground">{p.desc}</p>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  ) : null}

                  <div className="flex justify-between pt-2">
                    <Button variant="ghost" onClick={() => { setAddSecond(null); prev(); }} className="gap-1">
                      <ChevronLeft className="h-4 w-4" /> Voltar
                    </Button>
                    <Button onClick={goToDashboard} className="gap-2 glow-orange-sm">
                      <Sparkles className="h-4 w-4" />
                      Ir para o Dashboard
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </Slide>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
